import React, { useEffect, useState } from 'react'
import { Loading, useTraversal } from '@guillotinaweb/react-gmi'

const operations = ['Allow', 'Deny', 'AllowSingle', 'Unset']
const permissionTypes = [
  { text: 'Choose..', value: '' },
  { text: 'Role Permissions', value: 'roleperm' },
  { text: 'Principal Permissions', value: 'prinperm' },
  { text: 'Principal Roles', value: 'prinrole' },
]

const objectOrEmpty = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}

const normalizeSharing = (sharing) => {
  const local = objectOrEmpty(sharing?.local)

  return {
    local: {
      roleperm: objectOrEmpty(local.roleperm),
      prinperm: objectOrEmpty(local.prinperm),
      prinrole: objectOrEmpty(local.prinrole),
    },
    inherit: objectOrEmpty(sharing?.inherit),
  }
}

const toOptions = (items) =>
  items.map((item) => {
    const value = item.id || item
    return { text: value, value }
  })

function PermissionTable({ title, headers, data, emptyMessage }) {
  const entries = Object.entries(data)

  return (
    <>
      <h2 className="title is-size-5 has-text-grey-dark">{title}</h2>
      <table className="table is-fullwidth is-striped is-hoverable is-size-7">
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {entries.map(([principal, values], idx) => (
            <React.Fragment key={`${principal}-${idx}`}>
              <tr>
                <td colSpan="3" className="has-text-link">
                  {principal}
                </td>
              </tr>
              {Object.entries(objectOrEmpty(values)).map(([row, setting]) => (
                <tr key={`${principal}-${row}`}>
                  <td></td>
                  <td>{row}</td>
                  <td>{setting}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
          {entries.length === 0 && (
            <tr>
              <td colSpan="3">{emptyMessage}</td>
            </tr>
          )}
        </tbody>
      </table>
    </>
  )
}

function SelectField({ label, value, options, multiple = false, onChange }) {
  return (
    <div className="field">
      <label className="label">{label}</label>
      <div className="select is-small is-fullwidth" style={{ height: 'auto' }}>
        <select
          multiple={multiple}
          value={value}
          onChange={(ev) => {
            if (multiple) {
              onChange(
                Array.from(ev.target.selectedOptions).map((o) => o.value)
              )
            } else {
              onChange(ev.target.value)
            }
          }}
        >
          {!multiple && <option value="">Choose..</option>}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.text}
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}

function AddPermission({ onSaved }) {
  const Ctx = useTraversal()
  const [state, setState] = useState({
    current: '',
    role: '',
    principal: '',
    permissions: [],
    roles: [],
    setting: '',
  })
  const [options, setOptions] = useState({
    permissions: [],
    roles: [],
    principals: [],
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState()

  useEffect(() => {
    let cancelled = false

    async function init() {
      const [permissions, groupsRes, rolesRes] = await Promise.all([
        Ctx.client.getAllPermissions(Ctx.path),
        Ctx.client.getGroups(Ctx.path),
        Ctx.client.getRoles(Ctx.path),
      ])

      if (cancelled) return

      const groups = groupsRes.status < 400 ? await groupsRes.json() : []
      const roles = rolesRes.status < 400 ? await rolesRes.json() : []
      setOptions({
        permissions: toOptions(permissions),
        principals: toOptions(groups),
        roles: toOptions(roles),
      })
    }

    init()

    return () => {
      cancelled = true
    }
  }, [Ctx])

  const update = (data) => setState((current) => ({ ...current, ...data }))

  const savePermission = async () => {
    const { current, role, principal, permissions, roles, setting } = state

    if (!current || !setting) {
      setError('Invalid form data')
      return
    }

    let data
    if (current === 'roleperm' && role && permissions.length > 0) {
      data = {
        roleperm: permissions.map((permission) => ({
          role,
          permission,
          setting,
        })),
      }
    } else if (current === 'prinperm' && principal && permissions.length > 0) {
      data = {
        prinperm: permissions.map((permission) => ({
          principal,
          permission,
          setting,
        })),
      }
    } else if (current === 'prinrole' && principal && roles.length > 0) {
      data = {
        prinrole: roles.map((role) => ({
          principal,
          role,
          setting,
        })),
      }
    } else {
      setError('Invalid form data')
      return
    }

    setLoading(true)
    setError()
    const res = await Ctx.client.post(`${Ctx.path}@sharing`, data)
    setLoading(false)

    if (res.status >= 400) {
      setError('Invalid form data')
      return
    }

    onSaved()
  }

  return (
    <div className="column is-4 is-size-7">
      <h1 className="title is-size-5">Add Permissions</h1>
      {error && <div className="notification is-danger is-light">{error}</div>}
      <SelectField
        label="Select a type"
        value={state.current}
        options={permissionTypes}
        onChange={(current) => update({ current })}
      />
      <hr />
      {state.current === 'roleperm' && (
        <SelectField
          label="Select a Role"
          value={state.role}
          options={options.roles}
          onChange={(role) => update({ role })}
        />
      )}
      {(state.current === 'prinperm' || state.current === 'prinrole') && (
        <SelectField
          label="Select a Principal"
          value={state.principal}
          options={options.principals}
          onChange={(principal) => update({ principal })}
        />
      )}
      {(state.current === 'roleperm' || state.current === 'prinperm') && (
        <SelectField
          label="Select a Permission"
          value={state.permissions}
          options={options.permissions}
          multiple
          onChange={(permissions) => update({ permissions })}
        />
      )}
      {state.current === 'prinrole' && (
        <SelectField
          label="Select a Role"
          value={state.roles}
          options={options.roles}
          multiple
          onChange={(roles) => update({ roles })}
        />
      )}
      {state.current && (
        <SelectField
          label="Select an Operation"
          value={state.setting}
          options={operations.map((operation) => ({
            text: operation,
            value: operation,
          }))}
          onChange={(setting) => update({ setting })}
        />
      )}
      {state.current && (
        <button
          className={`button is-primary is-small ${
            loading ? 'is-loading' : ''
          }`}
          onClick={savePermission}
        >
          Save
        </button>
      )}
    </div>
  )
}

export function SafePanelPermissions() {
  const Ctx = useTraversal()
  const [sharing, setSharing] = useState(normalizeSharing())
  const [loading, setLoading] = useState(true)
  const [reset, setReset] = useState(1)

  useEffect(() => {
    let cancelled = false

    async function loadSharing() {
      setLoading(true)
      const res = await Ctx.client.get(`${Ctx.path}@sharing`)
      const data = await res.json()

      if (!cancelled) {
        setSharing(normalizeSharing(data))
        setLoading(false)
      }
    }

    loadSharing()

    return () => {
      cancelled = true
    }
  }, [Ctx, reset])

  return (
    <div className="columns">
      <div className="column is-8 is-size-7 permissions">
        {loading ? (
          <Loading />
        ) : (
          <>
            <PermissionTable
              title="Role Permissions"
              headers={['Role', 'Permission', 'Setting']}
              data={sharing.local.roleperm}
              emptyMessage="No roles permissions defined"
            />
            <PermissionTable
              title="Principal Permissions"
              headers={['Principal', 'Permission', 'Setting']}
              data={sharing.local.prinperm}
              emptyMessage="No principals permissions defined"
            />
            <PermissionTable
              title="Principal Roles"
              headers={['Principal', 'Role', 'Setting']}
              data={sharing.local.prinrole}
              emptyMessage="No principals roles defined"
            />
          </>
        )}
      </div>
      {Ctx.hasPerm('guillotina.ChangePermissions') && (
        <AddPermission onSaved={() => setReset(Math.random())} />
      )}
    </div>
  )
}
